import type {
  PublicShare,
  QuoteOutputProjection,
} from '../api/active/generated/quote';

/** Public read model: no local quote, catalog, phone, SKU or internal versions. */
export type PublicShareModel = {
  quoteNo: string;
  customerName: string;
  customerVersionNo: string;
  pricingView: 'purchase' | 'rental';
  pricingVisibility: 'visible' | 'restricted';
  quoteExpiresAt: string;
  effectiveExpiresAt: string;
  totalCents?: number;
  styleId: string;
  styleVersion: string;
  fieldCombinationId: string;
  fieldCombinationVersion: string;
  outputProjection: QuoteOutputProjection;
  items: {
    itemId: string;
    name: string;
    quantity: number;
    unitPriceCents?: number;
    lineTotalCents?: number;
  }[];
};

function money(value: unknown): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0)
    throw new Error('公开报价金额合同无效');
  return value;
}

function text(value: unknown): string {
  if (typeof value !== 'string' || !value.trim())
    throw new Error('公开报价字段缺失');
  return value;
}

function instant(value: unknown): string {
  const result = text(value);
  if (!Number.isFinite(Date.parse(result)))
    throw new Error('公开报价有效期无效');
  return result;
}

export function mapPublicShare(value: PublicShare): PublicShareModel {
  if (
    value.timeZone !== 'Asia/Shanghai' ||
    value.currency !== 'CNY' ||
    !['purchase', 'rental'].includes(value.pricingView) ||
    !['visible', 'restricted'].includes(value.pricingVisibility) ||
    !/^[1-9][0-9]*$/.test(value.customerVersionNo) ||
    !Array.isArray(value.items) ||
    typeof value.outputProjection !== 'object' ||
    value.outputProjection === null ||
    value.outputProjection.kind !== 'quote-output-projection-v1' ||
    value.outputProjection.style.id !== value.styleId ||
    value.outputProjection.style.version !== value.styleVersion ||
    value.outputProjection.fieldCombination.id !== value.fieldCombinationId ||
    value.outputProjection.fieldCombination.version !==
      value.fieldCombinationVersion
  )
    throw new Error('公开报价合同不受支持');
  const visible = value.pricingVisibility === 'visible';
  const outputProjection = structuredClone(value.outputProjection);
  if (!visible) {
    const moneyKeys = new Set(
      outputProjection.columns
        .filter((column) => column.valueType === 'money')
        .map((column) => column.key),
    );
    outputProjection.columns = outputProjection.columns.filter(
      (column) => !moneyKeys.has(column.key),
    );
    outputProjection.fieldCombination.columns =
      outputProjection.fieldCombination.columns.filter(
        (column) => !moneyKeys.has(column.key),
      );
    outputProjection.rows = outputProjection.rows.map((row) => ({
      ...row,
      values: Object.fromEntries(
        Object.entries(row.values).filter(([key]) => !moneyKeys.has(key)),
      ),
    }));
    outputProjection.businessSections = outputProjection.businessSections.map(
      (section) => ({
        ...section,
        values: Object.fromEntries(
          Object.entries(section.values).filter(
            ([, sectionValue]) => typeof sectionValue !== 'number',
          ),
        ),
      }),
    );
  }
  const ids = new Set<string>();
  return {
    quoteNo: text(value.quoteNo),
    customerName: text(value.customerName),
    customerVersionNo: value.customerVersionNo,
    pricingView: value.pricingView,
    pricingVisibility: value.pricingVisibility,
    quoteExpiresAt: instant(value.quoteExpiresAt),
    effectiveExpiresAt: instant(value.effectiveExpiresAt),
    ...(visible ? { totalCents: money(value.totalCents) } : {}),
    styleId: text(value.styleId),
    styleVersion: text(value.styleVersion),
    fieldCombinationId: text(value.fieldCombinationId),
    fieldCombinationVersion: text(value.fieldCombinationVersion),
    outputProjection,
    items: value.items.map((item) => {
      const itemId = text(item.itemId);
      if (
        ids.has(itemId) ||
        !Number.isSafeInteger(item.quantity) ||
        item.quantity < 1
      )
        throw new Error('公开报价条目无效');
      ids.add(itemId);
      return {
        itemId,
        name: text(item.name),
        quantity: item.quantity,
        ...(visible
          ? {
              unitPriceCents: money(item.unitPriceCents),
              lineTotalCents: money(item.lineTotalCents),
            }
          : {}),
      };
    }),
  };
}

export function formatPublicMoney(cents: number): string {
  const digits = String(money(cents)).padStart(3, '0');
  return `¥${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

export { formatBeijingInstant } from './beijingTime';
